/**
 * The parish data sheet's fields (release 19, owner 2026-10-08), read the same way by every screen,
 * print, download, filter and report: the religion a person holds when none is recorded, and the
 * income band of a typed amount. Pure.
 */
import { AGAMA_UMAT, type PENDAPATAN_BULANAN, type STATUS_GEREJA } from './schema/contract'

export type AgamaUmat = (typeof AGAMA_UMAT)[number]
export type PendapatanBulanan = (typeof PENDAPATAN_BULANAN)[number]
type StatusGereja = (typeof STATUS_GEREJA)[number]

/**
 * A person's religion: the recorded one; without it Katolik for a Catholic (`status_gereja`
 * katolik), the rows from before schema 15 included; nothing for a katekumen or a non-Catholic
 * whose religion nobody recorded. A katekumen holds no Katolik religion yet (review minor 4,
 * ruling 2026-10-09): one a form once wrote reads as not recorded, as every other katekumen's.
 */
export function agamaUmat(agama: unknown, statusGereja: unknown): AgamaUmat | null {
  if (typeof agama === 'string' && (AGAMA_UMAT as readonly string[]).includes(agama)) {
    if (agama === 'katolik' && statusGereja === 'katekumen') return null
    return agama as AgamaUmat
  }
  return statusGereja === 'katolik' ? 'katolik' : null
}

/**
 * The band of a monthly income in rupiah (the sheet's "Pendapatan Perbulan" when a number was
 * typed): the lower edge belongs to its band, the upper one to the next; 6 juta is still "4–6 jt",
 * "> 6 jt" being more than 6 juta. Null for a negative amount or not a number.
 */
export function bandPendapatan(rupiah: number): PendapatanBulanan | null {
  if (!Number.isFinite(rupiah) || rupiah < 0) return null
  if (rupiah < 500_000) return 'kurang_500rb'
  if (rupiah < 1_000_000) return '500rb_1jt'
  if (rupiah < 2_000_000) return '1jt_2jt'
  if (rupiah < 3_000_000) return '2jt_3jt'
  if (rupiah < 4_000_000) return '3jt_4jt'
  if (rupiah <= 6_000_000) return '4jt_6jt'
  return 'lebih_6jt'
}

/**
 * A person's religion and Status gereja agree (release 19): a Catholic's religion is Katolik, a
 * non-Catholic's is not. When a form leaves them at odds, the one the clerk changed decides: a
 * religion other than Katolik makes a Catholic "Bukan Katolik", Katolik makes a non-Catholic
 * Catholic; a Status gereja changed alone takes Katolik with it, or drops a Katolik religion
 * (the other religion is then not known). A katekumen keeps the religion they hold until baptism,
 * never Katolik (not yet held: left not recorded, review minor 4). Values that agree are returned
 * as they are.
 */
export function selaraskanAgama(input: {
  agama: AgamaUmat | null
  statusGereja: StatusGereja
  /** The clerk changed the religion (a form without Status gereja: always). */
  agamaDiubah: boolean
}): { agama: AgamaUmat | null; statusGereja: StatusGereja } {
  const { agama, statusGereja, agamaDiubah } = input
  // A katekumen is given no Katolik religion: it is left not recorded (review minor 4).
  if (statusGereja === 'katekumen' && agama === 'katolik') return { agama: null, statusGereja }
  if (statusGereja === 'katolik' && agama !== null && agama !== 'katolik') {
    return agamaDiubah ? { agama, statusGereja: 'non_katolik' } : { agama: 'katolik', statusGereja }
  }
  if (statusGereja === 'non_katolik' && agama === 'katolik') {
    return agamaDiubah ? { agama, statusGereja: 'katolik' } : { agama: null, statusGereja }
  }
  return { agama, statusGereja }
}

/**
 * The age bands Daftar Umat offers and Profil umat counts by (release 19: one list for both, so a
 * report row opens the list it counts): `dari-sampai` in completed years, the last open-ended.
 */
export const KELOMPOK_USIA = ['0-6', '7-12', '13-17', '18-35', '36-59', '60-'] as const
export type KelompokUsia = (typeof KELOMPOK_USIA)[number]

/** The band of an age in completed years. */
export function kelompokUsiaDari(usia: number): KelompokUsia {
  for (const k of KELOMPOK_USIA) {
    const [dari, sampai] = k.split('-')
    if (usia >= Number(dari) && (sampai === '' || usia <= Number(sampai))) return k
  }
  return '0-6'
}
